import { Box, Typography } from '@mui/material';
import { GeoJSONFeature, GeoJSONFeatureCollection } from 'ol/format/GeoJSON';
import { Fragment, useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import DeviceBlock from '../../components/device-block/device-block.component';
import DevicesetTable from '../../components/deviceset-table/deviceset-table.component';
import {
    BoxFullWH,
    FlexBoxFullW,
    FlexBoxFullWHColumn,
    FlexBoxFullWHRow,
    FlexBoxFullWRowBetween,
} from '../../components/layout/layout.styles';
import MapWrapper from '../../components/map/map-wrapper/map-wrapper.component';
import BackButton from '../../components/ui/back-button/back-button.component';
import HomeButton from '../../components/ui/home-button/home-button.component';
import { CONTENT_TYPES } from '../../constants/data.constants';
import { LOGIN_PAGE_ROUTE } from '../../constants/routes.constants';
import {
    DeviceProperties,
    DeviceSetProperties,
    StackParams,
    StatusSeverity,
} from '../../models/models';
import { selectAccessToken } from '../../store/auth/auth.selector';
import { getDeviceStart } from '../../store/device/device.action';
import {
    getDevicesetsStart,
    getDevicesetsStatusStart,
} from '../../store/deviceset/deviceset.action';
import { AnimatedContainer, DataColumn } from './home.styles';

const HomePage = () => {
    const [mapExpanded, setMapExpanded] = useState(false);
    const [dataVisible, setDataVisible] = useState(true);
    const [devicesets, setDevicesets] = useState<GeoJSONFeatureCollection>();
    const [device, setDevice] = useState<GeoJSONFeature>();
    const [stackParams, setStackParams] = useState<StackParams[]>([]);
    const [showCharts, setShowCharts] = useState(false);

    const dispatch = useDispatch();

    const accessToken = useSelector(selectAccessToken);
    const navigate = useNavigate();

    const location = useLocation();

    const getDeviceSets = useCallback(
        (
            devicesetId: string | null = null,
            params: StackParams | null = null,
            direction: 'forward' | 'back'
        ) => {
            dispatch(
                getDevicesetsStart(devicesetId, (newDevicesets) => {
                    setDevicesets(newDevicesets);
                    setDevice(undefined);
                    if (direction === 'back') {
                        setStackParams((prev) => prev.slice(0, -1));
                    } else {
                        if (params) {
                            setStackParams((prev) => [
                                ...prev,
                                {
                                    content_type: params.content_type,
                                    id: params.id,
                                    name: params.name,
                                },
                            ]);
                        }
                    }
                })
            );
        },
        [dispatch]
    );

    const getDevice = useCallback(
        (
            deviceId: string,
            params: StackParams,
            direction: 'forward' | 'back'
        ) => {
            dispatch(
                getDeviceStart(deviceId, (device) => {
                    setDevicesets(undefined);
                    setDevice(device);
                    if (direction === 'back') {
                        setStackParams((prev) => prev.slice(0, -1));
                    } else {
                        setStackParams((prev) => [
                            ...prev,
                            {
                                content_type: params.content_type,
                                id: params.id,
                                name: params.name,
                            },
                        ]);
                    }
                })
            );
        },
        [dispatch]
    );

    const handleMapExpandedChange = () => {
        if (mapExpanded) {
            setMapExpanded(false);
            setTimeout(() => setDataVisible(true), 500);
        } else {
            setDataVisible(false);
            setTimeout(() => setMapExpanded(true), 0);
        }
    };

    const handleOnBack = () => {
        const currentStackParams = stackParams.slice(0, -1);
        if (currentStackParams.length > 0) {
            const params = currentStackParams[currentStackParams.length - 1];
            if (params.content_type === CONTENT_TYPES.DEVICESET) {
                getDeviceSets(params.id, params, 'back');
            } else if (
                params.content_type === CONTENT_TYPES.DEVICE &&
                params.id
            ) {
                getDevice(params.id, params, 'back');
            }
        } else {
            getDeviceSets(null, null, 'back');
        }
    };

    const handleOnHome = () => {
        setStackParams([]);
        getDeviceSets(null, null, 'forward');
    };

    const handleRowSelected = (row: StackParams) => {
        if (row.content_type === CONTENT_TYPES.DEVICESET) {
            getDeviceSets(
                row.id,
                {
                    id: row.id,
                    content_type: row.content_type,
                    name: row.name,
                },
                'forward'
            );
        } else if (row.content_type === CONTENT_TYPES.DEVICE) {
            getDevice(
                row.id,
                {
                    id: row.id,
                    content_type: row.content_type,
                    name: row.name,
                },
                'forward'
            );
        }
    };

    useEffect(() => {
        if (!accessToken && location.pathname !== '/sso') {
            navigate(LOGIN_PAGE_ROUTE);
        }
    }, [accessToken, navigate, location.pathname]);

    useEffect(() => {
        getDeviceSets(null, null, 'forward');
    }, [getDeviceSets]);

    useEffect(() => {
        if (devicesets) {
            const intervalId = setInterval(() => {
                const devicesetId =
                    stackParams.length > 0
                        ? stackParams[stackParams.length - 1].id
                        : null;
                dispatch(
                    getDevicesetsStatusStart(devicesetId, (status) => {
                        Object.keys(status).forEach((key) => {
                            setDevicesets((prev: GeoJSONFeatureCollection) => {
                                if (!prev) return prev;
                                const newValue = Object.assign({}, prev);
                                newValue.features.forEach((feature: any) => {
                                    if (feature.id === key) {
                                        const properties =
                                            feature.properties as DeviceSetProperties;
                                        properties.details.status_severity =
                                            status[key];
                                    }
                                });
                                return newValue;
                            });
                        });
                    })
                );
            }, 5000);

            return () => clearInterval(intervalId);
        }
    }, [devicesets, stackParams, dispatch]);

    return (
        <FlexBoxFullWHRow gap={dataVisible ? 2 : 0}>
            {!showCharts && (
                <AnimatedContainer mapExpanded={mapExpanded}>
                    <FlexBoxFullWHColumn gap={2}>
                        <FlexBoxFullWRowBetween
                            height={100}
                            alignItems={'center'}
                        >
                            <Typography fontSize={25}>
                                {stackParams.length > 0
                                    ? stackParams[stackParams.length - 1].name
                                    : 'Home'}
                            </Typography>
                            {mapExpanded && stackParams.length > 0 && (
                                <Box display={'flex'}>
                                    <BackButton onClick={handleOnBack} />
                                    <Box width={15} />
                                    <HomeButton onClick={handleOnHome} />
                                </Box>
                            )}
                        </FlexBoxFullWRowBetween>
                        <MapWrapper
                            devicesets={devicesets}
                            device={device}
                            onMapExpandedChange={handleMapExpandedChange}
                            onDevicesetSelected={handleRowSelected}
                        />
                    </FlexBoxFullWHColumn>
                </AnimatedContainer>
            )}
            <DataColumn visible={dataVisible} showCharts={showCharts}>
                <FlexBoxFullWHColumn gap={2}>
                    {!showCharts && (
                        <FlexBoxFullW height={100} alignItems={'center'}>
                            {stackParams.length > 0 && (
                                <Fragment>
                                    <BackButton onClick={handleOnBack} />
                                    <Box width={15} />
                                    <HomeButton onClick={handleOnHome} />
                                </Fragment>
                            )}
                        </FlexBoxFullW>
                    )}
                    <BoxFullWH overflow={'hidden'}>
                        {devicesets && (
                            <DevicesetTable
                                devicesets={devicesets}
                                onRowSelected={handleRowSelected}
                            />
                        )}
                        {device && (
                            <DeviceBlock
                                device={device}
                                showCharts={showCharts}
                                onChartsVisibilityChange={() =>
                                    setShowCharts(!showCharts)
                                }
                                onDeviceStatusSeverityChange={(
                                    status: StatusSeverity
                                ) => {
                                    setDevice((prev: GeoJSONFeature) => {
                                        if (!prev) return prev;
                                        const newValue = Object.assign(
                                            {},
                                            prev
                                        );
                                        const properties =
                                            newValue.properties as DeviceProperties;
                                        properties.details.status_severity =
                                            status;
                                        return newValue;
                                    });
                                }}
                            />
                        )}
                    </BoxFullWH>
                </FlexBoxFullWHColumn>
            </DataColumn>
        </FlexBoxFullWHRow>
    );
};

export default HomePage;
